'use client';

import { useEffect, useState, type ComponentProps } from 'react';

import { HeaderSection } from '@/vibes/soul/sections/header-section';

interface AuthSessionResponse {
  user?: {
    firstName?: string | null;
    email?: string | null;
  } | null;
}

interface CartCountResponse {
  count?: number;
}

type HeaderNavigationProps = ComponentProps<typeof HeaderSection>['navigation'];
type StaticHeaderNavigationProps = Omit<HeaderNavigationProps, 'accountUser' | 'cartCount'>;

interface Props {
  navigation: StaticHeaderNavigationProps;
}

export function HeaderClient({ navigation }: Props) {
  const [accountUser, setAccountUser] = useState<{
    firstName: string | null;
    email: string | null;
  } | null>(null);
  const [cartCount, setCartCount] = useState<number | null>(null);

  useEffect(() => {
    let isCancelled = false;

    const loadRuntimeState = async () => {
      const [sessionResult, cartCountResult] = await Promise.allSettled([
        fetch('/api/auth/session/', {
          cache: 'no-store',
          credentials: 'same-origin',
        }),
        fetch('/api/cart/count/', {
          cache: 'no-store',
          credentials: 'same-origin',
        }),
      ]);

      if (
        sessionResult.status === 'fulfilled' &&
        sessionResult.value.ok &&
        !isCancelled
      ) {
        try {
          const session = (await sessionResult.value.json()) as AuthSessionResponse | null;
          const user = session?.user;

          setAccountUser(
            user
              ? {
                  firstName: user.firstName ?? null,
                  email: user.email ?? null,
                }
              : null,
          );
        } catch (error) {
          console.error('Failed to parse auth session response', error);
        }
      }

      if (
        cartCountResult.status === 'fulfilled' &&
        cartCountResult.value.ok &&
        !isCancelled
      ) {
        try {
          const response = (await cartCountResult.value.json()) as CartCountResponse;
          setCartCount(typeof response.count === 'number' ? response.count : null);
        } catch (error) {
          console.error('Failed to parse cart count response', error);
        }
      }
    };

    void loadRuntimeState();

    return () => {
      isCancelled = true;
    };
  }, []);

  return (
    <HeaderSection
      navigation={{
        ...navigation,
        accountUser,
        cartCount,
      }}
    />
  );
}
